import { BaseRepository } from './BaseRepository';
import { supabase } from '@/lib/supabase';

export interface IntegrationOperation {
  id: string;
  provider_id: string;
  key: string;
  direction: string;
  family: string;
  graphql_name: string;
  input_type: string | null;
  sample_query: string;
  sample_variables_json: any;
  description: string;
  created_at?: string;
  updated_at?: string;
}

export interface IntegrationProvider {
  id: string;
  name: string;
  slug: string;
  description?: string;
}

export class IntegrationOperationsRepository extends BaseRepository<IntegrationOperation> {
  constructor() {
    super({ tableName: 'integration_operations' });
  }

  /**
   * Get provider by slug
   */
  async getProviderBySlug(slug: string): Promise<IntegrationProvider | null> {
    if (!supabase) return null;

    const { data, error } = await supabase
      .from('integration_providers')
      .select('*')
      .eq('slug', slug)
      .single();

    if (error) {
      console.error('Error fetching provider:', error);
      return null;
    }

    return data;
  }

  /**
   * Get all operations for a specific provider
   */
  async getOperationsByProvider(providerId: string): Promise<IntegrationOperation[]> {
    if (!supabase) return [];

    const { data, error } = await supabase
      .from(this.config.tableName)
      .select('*')
      .eq('provider_id', providerId)
      .order('family', { ascending: true })
      .order('key', { ascending: true });

    if (error) {
      console.error('Error fetching operations:', error);
      return [];
    }

    return data || [];
  }

  /**
   * Get all operations for SuccessWare provider
   */
  async getSuccessWareOperations(): Promise<IntegrationOperation[]> {
    const provider = await this.getProviderBySlug('successware');
    if (!provider) return [];

    return this.getOperationsByProvider(provider.id);
  }

  /**
   * Get operation by ID
   */
  async getOperationById(id: string): Promise<IntegrationOperation | null> {
    if (!supabase) return null;

    const { data, error } = await supabase
      .from(this.config.tableName)
      .select('*')
      .eq('id', id)
      .single();

    if (error) {
      console.error('Error fetching operation:', error);
      return null;
    }

    return data;
  }

  /**
   * Search operations by text
   */
  async searchOperations(
    providerId: string,
    searchText: string
  ): Promise<IntegrationOperation[]> {
    if (!supabase) return [];

    const { data, error } = await supabase
      .from(this.config.tableName)
      .select('*')
      .eq('provider_id', providerId)
      .or(
        `key.ilike.%${searchText}%,graphql_name.ilike.%${searchText}%,description.ilike.%${searchText}%`
      )
      .order('family', { ascending: true })
      .order('key', { ascending: true });

    if (error) {
      console.error('Error searching operations:', error);
      return [];
    }

    return data || [];
  }

  /**
   * Get operations by direction (push/pull)
   */
  async getOperationsByDirection(
    providerId: string,
    direction: string
  ): Promise<IntegrationOperation[]> {
    if (!supabase) return [];

    const { data, error } = await supabase
      .from(this.config.tableName)
      .select('*')
      .eq('provider_id', providerId)
      .eq('direction', direction)
      .order('family', { ascending: true })
      .order('key', { ascending: true });

    if (error) {
      console.error('Error fetching operations by direction:', error);
      return [];
    }

    return data || [];
  }

  /**
   * Get operations by family (core/bookit)
   */
  async getOperationsByFamily(
    providerId: string,
    family: string
  ): Promise<IntegrationOperation[]> {
    if (!supabase) return [];

    const { data, error } = await supabase
      .from(this.config.tableName)
      .select('*')
      .eq('provider_id', providerId)
      .eq('family', family)
      .order('key', { ascending: true });

    if (error) {
      console.error('Error fetching operations by family:', error);
      return [];
    }

    return data || [];
  }
}

export const integrationOperationsRepository = new IntegrationOperationsRepository();
